import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { GuideOp, LIMITS } from './model';
import type { PlaceSubmitResult } from './AddPlaceDialog';

/** A new chapter at the end of the guide. The server derives its id, so it is saved at once. */
export default function ChapterDialog({
  open,
  pendingCount,
  onClose,
  onSubmit,
}: {
  open: boolean;
  pendingCount: number;
  onClose: () => void;
  onSubmit: (op: GuideOp) => Promise<PlaceSubmitResult>;
}) {
  const [title, setTitle] = useState('');
  const [kicker, setKicker] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTitle('');
      setKicker('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const valid = title.trim().length > 0 && title.length <= LIMITS['chapter.title'] && kicker.length <= LIMITS['chapter.kicker'];

  const submit = async () => {
    setBusy(true);
    setError(null);
    const op: GuideOp = { op: 'add_chapter', title: title.trim() };
    if (kicker.trim()) op.kicker = kicker.trim();
    const outcome = await onSubmit(op);
    setBusy(false);
    if (!outcome.ok) setError(outcome.message || 'The chapter could not be added.');
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add a chapter</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            label="Chapter title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            autoFocus
            size="small"
            helperText={`${title.length}/${LIMITS['chapter.title']}`}
          />
          <TextField
            label="Kicker (optional)"
            value={kicker}
            onChange={(e) => setKicker(e.target.value)}
            size="small"
            helperText={`The short label in the index. Defaults to the title. ${kicker.length}/${LIMITS['chapter.kicker']}`}
          />
          {pendingCount > 0 && (
            <Alert severity="info">
              Your {pendingCount} unsaved {pendingCount === 1 ? 'change is' : 'changes are'} saved
              together with the chapter.
            </Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={!valid || busy}>
          Add chapter
        </Button>
      </DialogActions>
    </Dialog>
  );
}
